import React, { useEffect, useState } from "react";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Grid,
  Paper,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import StatCard from "./StatCard";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { updateGroup } from "../../../../../api/updateGroups";
import { setGroups } from "../../../../../redux/slices/groupsSlice";
import { getTeamById, getTutorNameById, formatDate } from "../../../../../utils/getEntitiesUtils";
import { PreReportStatus } from "../../../PreReportFeedback/PreReportFeedbackHistory";
import { DownloadButtonWithSpinner } from "../../../../Buttons/CustomButtons";
import MySnackbar from "../../../MySnackBar";
import ReviewerNoticeDialog from "./ReviewerNoticeDialog";
import { Button, Chip, Tooltip, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useNavigate } from "react-router-dom";
import { FINAL_DOCUMENTS } from "../../../../../utils/finalDocuments";
import {
  allApproved,
  getFinalDeliverySummary,
  unmarkWithdrawal,
} from "../../../../../api/finalDelivery";
import FinalEvaluatorsDialog from "./FinalEvaluatorsDialog";
import WithdrawalDialog from "./WithdrawalDialog";
import FinalDeliveryNoticeDialog from "./FinalDeliveryNoticeDialog";

const ContentPdfProjects = ({
  loadingProjects,
  deliveries,
  downloadFile,
  projectType,
}) => {
  let teams = Object.values(useSelector((state) => state.groups))
    .sort((a, b) => a.id - b.id)
    .map(({ version, rehydrated, ...rest }) => rest) // Filtra las propiedades 'version' y 'rehydrated'
    .filter((item) => Object.keys(item).length > 0); // Elimina objetos vacíos
  const tutors = Object.values(useSelector((state) => state.tutors))
    .map(({ version, rehydrated, ...rest }) => rest) // Filtra las propiedades 'version' y 'rehydrated'
    .filter((item) => Object.keys(item).length > 0); // Elimina objetos vacíos
  const user = useSelector((state) => state.user);
  const period = useSelector((state) => state.period);
  const [selectedReviewers, setSelectedReviewers] = useState({});
  const [downloading, setDownloading] = useState(null)
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    status: "",
  });
  const dispatch = useDispatch();

  const handleSnackbarClose = () => {
    setNotification({ ...notification, open: false });
  };

  // Aviso de revisor asignado: que equipo tiene el dialogo abierto.
  const [noticeTeam, setNoticeTeam] = useState(null);
  const navigate = useNavigate();

  // Entrega final: evaluadores y ultimo aviso por equipo ({ [group_id]: {...} }),
  // y que equipo tiene abierto cada dialogo. Se pide una vez para todo el
  // cuatrimestre; los dialogos actualizan este estado en lugar de recargarlo.
  const [finalSummary, setFinalSummary] = useState({});
  const [evaluatorsTeam, setEvaluatorsTeam] = useState(null);
  const [finalNoticeTeam, setFinalNoticeTeam] = useState(null);
  // Equipo para el que está abierto "marcar que no entrega".
  const [withdrawalTeam, setWithdrawalTeam] = useState(null);

  useEffect(() => {
    if (projectType !== "final" || !period?.id) return;
    getFinalDeliverySummary(user, period.id)
      .then((rows) =>
        setFinalSummary(Object.fromEntries(rows.map((row) => [row.group_id, row])))
      )
      .catch((error) => {
        console.error("Error al cargar los evaluadores:", error);
        setNotification({
          open: true,
          message: "No se pudieron cargar los evaluadores de la entrega final.",
          status: "error",
        });
      });
  }, [projectType, period?.id, user]);

  const evaluatorsOf = (team) => finalSummary[team?.id]?.evaluators || [];

  const handleEvaluatorsChange = (teamId, evaluators) =>
    setFinalSummary((prev) => ({
      ...prev,
      [teamId]: { ...(prev[teamId] || { group_id: teamId }), evaluators },
    }));

  const handleFinalNoticeSent = (teamId, result) => {
    setFinalSummary((prev) => ({
      ...prev,
      [teamId]: {
        ...(prev[teamId] || { group_id: teamId, evaluators: [] }),
        last_notice_at: result.sent_at,
      },
    }));
    setNotification({ open: true, message: result.detail, status: "success" });
  };

  const setWithdrawal = (teamId, withdrawn_at, withdrawal_reason) =>
    setFinalSummary((prev) => ({
      ...prev,
      [teamId]: {
        ...(prev[teamId] || { group_id: teamId, evaluators: [] }),
        withdrawn_at,
        withdrawal_reason,
      },
    }));

  const handleUnmarkWithdrawal = async (team) => {
    try {
      await unmarkWithdrawal(user, team.id);
      setWithdrawal(team.id, null, null);
    } catch (e) {
      setNotification({
        open: true,
        message: e?.response?.data?.detail || "No se pudo deshacer la marca.",
        status: "error",
      });
    }
  };

  // "No entrega": el equipo queda afuera del algoritmo de fechas y de la
  // disponibilidad. Se muestra arriba de los evaluadores.
  const renderWithdrawal = (team) => {
    const summary = finalSummary[team?.id];
    if (summary?.withdrawn_at) {
      return (
        <Box sx={{ mb: 0.5 }}>
          <Chip size="small" color="warning" label="No entrega" />
          {summary.withdrawal_reason && (
            <Typography variant="caption" color="text.secondary" component="div">
              {summary.withdrawal_reason}
            </Typography>
          )}
          <Button
            size="small"
            onClick={() => handleUnmarkWithdrawal(team)}
            sx={{ textTransform: "none", px: 0 }}
          >
            Deshacer
          </Button>
        </Box>
      );
    }
    return (
      <Button
        size="small"
        color="inherit"
        onClick={() => setWithdrawalTeam(team)}
        sx={{ textTransform: "none", px: 0, color: "text.secondary" }}
      >
        Marcar que no entrega
      </Button>
    );
  };

  const renderEvaluatorsCell = (team) => {
    const evaluators = evaluatorsOf(team);
    return (
      <TableCell>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.25}>
          {renderWithdrawal(team)}
          {evaluators.length > 0 && (
            <Typography
              variant="caption"
              color={allApproved(evaluators) ? "success.main" : "text.secondary"}
              fontWeight={allApproved(evaluators) ? "bold" : undefined}
            >
              {allApproved(evaluators)
                ? "Aprobada"
                : `Aprobaron ${evaluators.filter((e) => e.approved_at).length} de ${
                    evaluators.length
                  }`}
            </Typography>
          )}
          {evaluators.map((evaluator) => (
            <Typography key={evaluator.id} variant="body2">
              {evaluator.approved_at ? "✓ " : ""}
              {evaluator.name}
              {evaluator.is_external && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {" "}(externo)
                </Typography>
              )}
            </Typography>
          ))}
          <Button
            size="small"
            onClick={() => setEvaluatorsTeam(team)}
            sx={{ textTransform: "none", px: 0 }}
          >
            {evaluators.length ? "Editar" : "Asignar evaluadores"}
          </Button>
        </Box>
      </TableCell>
    );
  };

  const renderFinalNoticeCell = (team) => {
    const delivered = Boolean(team?.final_report_date);
    const hasEvaluators = evaluatorsOf(team).length > 0;
    const lastNotice = finalSummary[team?.id]?.last_notice_at;
    const reason = !delivered
      ? "El equipo todavía no completó la entrega final"
      : !hasEvaluators
      ? "Primero asigná al menos un evaluador"
      : lastNotice
      ? `Avisado el ${formatDate(lastNotice)}. Clic para ver el historial o reenviar.`
      : "Mandar el aviso con los links a estudiantes, director y evaluadores";

    return (
      <TableCell>
        <Tooltip title={reason}>
          <span>
            <Button
              size="small"
              variant={lastNotice ? "text" : "outlined"}
              startIcon={<MailOutlineIcon />}
              disabled={!delivered || !hasEvaluators}
              onClick={() => setFinalNoticeTeam(team)}
            >
              {lastNotice ? "Reenviar" : "Avisar"}
            </Button>
          </span>
        </Tooltip>
        {lastNotice && (
          <Typography variant="caption" display="block" color="text.secondary">
            Avisado el {formatDate(lastNotice)}
          </Typography>
        )}
      </TableCell>
    );
  };

  const handleNoticeSent = (teamId, result) => {
    const updatedTeams = teams.map((team) =>
      team.id === teamId
        ? { ...team, reviewer_notified_at: result.reviewer_notified_at }
        : team
    );
    dispatch(setGroups(updatedTeams));
    setNotification({
      open: true,
      message: result.detail,
      status: "success",
    });
  };

  // Valor del dropdown de revisor, siempre definido. Si esto es `undefined`
  // en el primer render (la tabla se dibuja con las entregas antes de que
  // llegue la lista de grupos), MUI toma el Select como no controlado y
  // despues ignora el valor real: el revisor queda en blanco para siempre
  // aunque este asignado. Paso el 2026-09-17 en produccion, en el primer
  // load despues de un restart, y parecia que se habian borrado.
  const reviewerValue = (key, team) => {
    if (selectedReviewers[key]) return selectedReviewers[key];
    const id = team?.reviewer_id;
    return id && id !== 0 ? id : "";
  };

  const renderNoticeCell = (team) => {
    const hasReviewer = team?.reviewer_id && team.reviewer_id !== 0;
    const hasPdf = Boolean(team?.pre_report_date);
    const reason = !hasPdf
      ? "El equipo todavía no subió el anteproyecto"
      : !hasReviewer
      ? "Primero asigná un revisor"
      : team.reviewer_notified_at
      ? `Avisado el ${formatDate(team.reviewer_notified_at)}. Clic para reenviar.`
      : "Mandar el aviso a estudiantes, director y revisor";

    return (
      <TableCell>
        <Tooltip title={reason}>
          <span>
            <Button
              size="small"
              variant={team?.reviewer_notified_at ? "text" : "outlined"}
              startIcon={<MailOutlineIcon />}
              disabled={!hasReviewer || !hasPdf}
              onClick={() => setNoticeTeam(team)}
            >
              {team?.reviewer_notified_at ? "Reenviar" : "Avisar"}
            </Button>
          </span>
        </Tooltip>
        {team?.reviewer_notified_at && (
          <Typography variant="caption" display="block" color="text.secondary">
            Avisado el {formatDate(team.reviewer_notified_at)}
          </Typography>
        )}
      </TableCell>
    );
  };

  // Estado de la revision del anteproyecto: aprobado / con devolucion / sin
  // revisar, con la fecha de la ultima interaccion. Sale de los mismos campos
  // que ve el tutor y el estudiante.
  const renderReviewCell = (team) => (
    <TableCell>
      <PreReportStatus
        history={{
          pre_report_approved: team?.pre_report_approved,
          pre_report_reviewed_at: team?.pre_report_reviewed_at,
        }}
      />
    </TableCell>
  );

  // Entrega final: un boton por documento, habilitado si el equipo lo subio.
  // Sirve tambien para los que no completaron, que pueden tener alguno.
  const renderFinalDownloadsCell = (team) => (
    <TableCell>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {FINAL_DOCUMENTS.map((document) => {
          const key = `${team?.id}/${document.key}`;
          return (
            <Tooltip
              key={document.key}
              title={
                team?.[document.dateField]
                  ? `Subido el ${formatDate(team[document.dateField])}`
                  : "Todavía no lo subió"
              }
            >
              <span>
                <Button
                  size="small"
                  startIcon={downloading === key ? <CircularProgress size={14} /> : <DownloadIcon />}
                  disabled={!team?.[document.dateField] || downloading === key}
                  onClick={async () => {
                    setDownloading(key);
                    await downloadFile(team.id, team.group_number, document.key);
                    setDownloading(null);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {document.label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </Box>
    </TableCell>
  );

  const [selectedEntregaron, setSelectedEntregaron] = useState(true);
  const [selectedNoEntregaron, setSelectedNoEntregaron] = useState(false);
  
  if (loadingProjects) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleReviewerChange = async (deliveryId, reviewerId) => {
    const previousReviewer = selectedReviewers[deliveryId];
    setSelectedReviewers({
      ...selectedReviewers,
      [deliveryId]: reviewerId,
    });
    // Obtener el equipo y crear una copia modificable
    const updatedTeam = { ...getTeamById(parseInt(deliveryId, 10), teams) };

    if (updatedTeam) {
      // Asignar el reviewerId a la copia del equipo
      updatedTeam.reviewer_id = reviewerId;

      try {
        // Llamar al backend para actualizar el equipo
        await updateGroup(user, period.id, updatedTeam);
      } catch (error) {
        // Antes no habia catch: un 409 quedaba en la consola del browser y el
        // dropdown mostraba el revisor como si se hubiera guardado. Un 409 trae
        // el motivo en `detail` (por ejemplo, capacidad del tutor); el resto se
        // reporta con un mensaje generico.
        console.error("Error al asignar revisor:", error);
        setSelectedReviewers({
          ...selectedReviewers,
          [deliveryId]: previousReviewer,
        });
        const detail = error?.response?.data?.detail;
        setNotification({
          open: true,
          message:
            error?.response?.status === 409 && detail
              ? `No se pudo asignar el revisor: ${detail}`
              : "No se pudo asignar el revisor. Inténtalo de nuevo.",
          status: "error",
        });
        return;
      }

      // Crear una nueva lista de equipos actualizados
      const updatedTeams = teams.map((team) =>
        team.id === updatedTeam.id ? updatedTeam : team
      );

      // Despachar la actualización solo del equipo modificado en Redux
      dispatch(setGroups(updatedTeams));
      setNotification({
        open: true,
        message: `Revisor asignado al Equipo ${updatedTeam.group_number}`,
        status: "success",
      });
    }
  };

  // La entrega final esta hecha cuando el equipo tiene los tres documentos, y
  // eso lo dice `final_report_date`, no el listado del storage (que es solo del
  // informe). Se arman entradas con la misma forma que las del listado para
  // reusar la tabla.
  if (projectType === "final") {
    deliveries = teams
      .filter((t) => !!t.final_report_date)
      .map((t) => ({
        name: `${period.id}/${t.id}/informe-final.pdf`,
        last_modified: t.final_report_date,
      }));
  }

  function getTeam(path) {
    const parts = path.split("/");
    return parts[1]; // Devuelve el equipo
  }

  function getTeamNumber(path) { // To-Do: función importable (usada tmb en TopicTutor)
    const parts = path.split("/");
    const team = teams?.find((g) => g.id === parseInt(parts[1]));
    return team ? team.group_number : null;
  }

  return (
    <div>
      {teams && (
        <Box mt={4}>
          {/* Tarjetas con cantidades */}          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <StatCard
                title="Equipos que entregaron"
                value={loadingProjects ? -1 : deliveries.length}
                onClick={() => {setSelectedEntregaron(true); setSelectedNoEntregaron(false)}} 
                selected={selectedEntregaron}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                title="Equipos que faltan entregar"
                value={
                  loadingProjects ? -1 : teams.length - deliveries.length
                }
                onClick={() => {setSelectedEntregaron(false); setSelectedNoEntregaron(true)}}
                selected={selectedNoEntregaron}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                title="Total de equipos"
                value={loadingProjects ? -1 : teams.length}
                onClick={() => {setSelectedEntregaron(true); setSelectedNoEntregaron(true)}}
                selected={selectedEntregaron && selectedNoEntregaron}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <TableContainer component={Paper} style={{ marginTop: "20px" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Equipo</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Tutor</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Titulo</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Fecha de Entrega
              </TableCell>
              {projectType === "initial" && (
                <TableCell sx={{ fontWeight: "bold" }}>Revisor</TableCell>
              )}
              {projectType === "initial" && (
                <TableCell sx={{ fontWeight: "bold" }}>Revisión</TableCell>
              )}
              {projectType === "initial" && (
                <TableCell sx={{ fontWeight: "bold" }}>
                  Aviso
                  <Button
                    size="small"
                    onClick={() => navigate("/plantilla-aviso-revisor")}
                    sx={{ ml: 1, textTransform: "none" }}
                  >
                    Editar plantilla
                  </Button>
                </TableCell>
              )}
              {projectType === "final" && (
                <TableCell sx={{ fontWeight: "bold" }}>Evaluadores</TableCell>
              )}
              {projectType === "final" && (
                <TableCell sx={{ fontWeight: "bold" }}>
                  Aviso
                  <Button
                    size="small"
                    onClick={() => navigate("/plantilla-aviso-entrega-final")}
                    sx={{ ml: 1, textTransform: "none" }}
                  >
                    Editar plantilla
                  </Button>
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: "bold" }}>Descargar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingProjects ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              <>
              {/* Contenido según la tarjeta clickeada (se muestran ambos bloques si ambos bools son true) */}
              
              {selectedEntregaron && (//Si es equipos que sí entregaron, muestro lo que había, que al
                deliveries.map((entrega, index) => ( // basarse en deliveries son justamente quienes sí entregaron
                  <TableRow key={index}>
                    <TableCell>{getTeamNumber(entrega.name)}</TableCell>
                    <TableCell>
                      {getTutorNameById(
                        teams.find(
                          (t) => parseInt(getTeam(entrega.name)) === t.id
                        )?.tutor_period_id, period.id,
                        tutors
                      )}
                    </TableCell>
                    <TableCell>
                      {projectType === "initial"
                        ? teams.find(
                            (t) => parseInt(getTeam(entrega.name)) === t.id
                          )?.pre_report_title ||
                          `Anteproyecto Equipo ${getTeamNumber(entrega.name)}`
                        : teams.find(
                            (t) => parseInt(getTeam(entrega.name)) === t.id
                          )?.final_report_title ||
                          `Proyecto Final Equipo ${getTeamNumber(entrega.name)}`}
                    </TableCell>
  
                    <TableCell>{formatDate(entrega.last_modified)}</TableCell>
                    {projectType === "initial" && (
                      <TableCell>
                        <Select
                          value={reviewerValue(
                            getTeam(entrega.name),
                            getTeamById(parseInt(getTeam(entrega.name), 10), teams)
                          )}
                          onChange={(e) =>
                            handleReviewerChange(
                              getTeam(entrega.name),
                              e.target.value
                            )
                          }
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Seleccionar Revisor
                          </MenuItem>
                          {tutors.map((tutor) => (
                            <MenuItem key={tutor.id} value={tutor.id}>
                              {tutor.name} {tutor.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                    {projectType === "initial" &&
                      renderReviewCell(
                        getTeamById(parseInt(getTeam(entrega.name), 10), teams)
                      )}
                    {projectType === "initial" &&
                      renderNoticeCell(
                        getTeamById(parseInt(getTeam(entrega.name), 10), teams)
                      )}
                    {projectType === "final" &&
                      renderEvaluatorsCell(
                        getTeamById(parseInt(getTeam(entrega.name), 10), teams)
                      )}
                    {projectType === "final" &&
                      renderFinalNoticeCell(
                        getTeamById(parseInt(getTeam(entrega.name), 10), teams)
                      )}
                    {projectType === "final" ? (
                      renderFinalDownloadsCell(
                        getTeamById(parseInt(getTeam(entrega.name), 10), teams)
                      )
                    ) : (
                    <TableCell>
                      <DownloadButtonWithSpinner
                        onClick={async () => {
                          setDownloading(entrega.name)
                          await downloadFile(getTeam(entrega.name), getTeamNumber(entrega.name))
                          setDownloading(null)
                        }}
                        spinningCondition={downloading === entrega.name}
                      />                      
                    </TableCell>
                    )}
                  </TableRow>
                )) // cierra deliveries.map

              )}


              {selectedNoEntregaron && (
                <>                
                {teams.filter((t) =>
                  projectType === "initial"
                  ? !t.pre_report_date
                  : !t.final_report_date
                  ).map((team) => (

                  <TableRow key={team.id}>
                    <TableCell>{team.group_number}</TableCell>
                    <TableCell>
                      {getTutorNameById(team.tutor_period_id, period.id, tutors)}
                    </TableCell>
                    <TableCell>
                      {projectType === "initial"
                        ? team.pre_report_title ||
                          `Anteproyecto Equipo ${team.group_number}`
                        : team.final_report_title ||
                          `Proyecto Final Equipo ${team.group_number}`}
                    </TableCell>
  
                    <TableCell> - </TableCell>
                    {projectType === "initial" && (
                      <TableCell>
                        <Select
                          value={reviewerValue(String(team.id), team)}
                          onChange={(e) =>
                            handleReviewerChange(
                              team,
                              e.target.value
                            )
                          }
                          displayEmpty
                          disabled //agrego
                        >
                          <MenuItem value="" disabled>
                            Seleccionar Revisor
                          </MenuItem>
                          {tutors.map((tutor) => (
                            <MenuItem key={tutor.id} value={tutor.id}>
                              {tutor.name} {tutor.last_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                    {projectType === "initial" && renderReviewCell(team)}
                    {projectType === "initial" && renderNoticeCell(team)}
                    {projectType === "final" && renderEvaluatorsCell(team)}
                    {projectType === "final" && renderFinalNoticeCell(team)}
                    {projectType === "final" ? (
                      renderFinalDownloadsCell(team)
                    ) : (
                    <TableCell>
                      <IconButton
                        disabled
                      >
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                    )}
                  </TableRow>
                  
                ))}
                </>
                
              )}

              </>

              
            )}
            
          </TableBody>
        </Table>
      </TableContainer>
      <ReviewerNoticeDialog
        open={Boolean(noticeTeam)}
        team={noticeTeam}
        onClose={() => setNoticeTeam(null)}
        onSent={handleNoticeSent}
        onEditTemplate={() => navigate("/plantilla-aviso-revisor")}
      />
      <WithdrawalDialog
        open={Boolean(withdrawalTeam)}
        team={withdrawalTeam}
        onClose={() => setWithdrawalTeam(null)}
        onMarked={(teamId, reason) => setWithdrawal(teamId, new Date().toISOString(), reason)}
      />
      <FinalEvaluatorsDialog
        open={Boolean(evaluatorsTeam)}
        team={evaluatorsTeam}
        evaluators={evaluatorsOf(evaluatorsTeam)}
        tutors={tutors}
        onClose={() => setEvaluatorsTeam(null)}
        onChange={handleEvaluatorsChange}
      />
      <FinalDeliveryNoticeDialog
        open={Boolean(finalNoticeTeam)}
        team={finalNoticeTeam}
        onClose={() => setFinalNoticeTeam(null)}
        onSent={handleFinalNoticeSent}
        onEditTemplate={() => navigate("/plantilla-aviso-entrega-final")}
      />
      <MySnackbar
        open={notification.open}
        handleClose={handleSnackbarClose}
        message={notification.message}
        status={notification.status}
      />
    </div>
  );
};

export default ContentPdfProjects;
